import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import {
  aggregateBenchmarkByInstitution,
  BENCHMARK_INSTITUTION_COLORS,
  BENCHMARK_PIE_STATS,
  formatBenchmarkChartValue,
} from "../../benchmarkMetricConfig";
import styles from "./PieChart.module.scss";

const SinglePie = ({ stat, institutions, colorMap, sliceLabels = "none" }) => {
  const chartRef = useRef(null);
  const instanceRef = useRef(null);

  const pieData = useMemo(
    () =>
      institutions.map((inst) => ({
        value: inst[stat.key] || 0,
        name: inst.name,
        itemStyle: {
          color: colorMap[inst.institution_id] || BENCHMARK_INSTITUTION_COLORS[0],
        },
      })),
    [institutions, stat.key, colorMap]
  );

  const total = useMemo(
    () => pieData.reduce((sum, d) => sum + d.value, 0),
    [pieData]
  );

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);
    instanceRef.current = chart;

    const showPercentOnly = sliceLabels === "percent";

    const option = {
      tooltip: {
        trigger: "item",
        backgroundColor: "#fff",
        borderColor: "rgba(0,0,0,0.08)",
        borderWidth: 1,
        padding: [12, 16],
        textStyle: {
          color: "#1a1a2e",
          fontSize: 13,
        },
        formatter: (params) => {
          const valStr = formatBenchmarkChartValue(params.value, stat.decimals ?? 0);
          return `<div style="font-weight:600;margin-bottom:4px">${params.name}</div>
                  <div style="display:flex;justify-content:space-between;gap:20px">
                    <span style="color:#666">${stat.label}</span>
                    <span style="font-weight:700;color:#0f3460">${valStr}</span>
                  </div>
                  <div style="color:#999;font-size:11px;margin-top:4px">${params.percent}% of total</div>`;
        },
        extraCssText:
          "border-radius:12px;box-shadow:0 8px 24px rgba(0,0,0,0.12);",
      },
      series: [
        {
          type: "pie",
          radius: ["42%", "72%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 6,
            borderColor: "#fff",
            borderWidth: 3,
          },
          label: showPercentOnly
            ? {
                show: true,
                position: "outside",
                formatter: "{d}%",
                fontSize: 11,
                fontWeight: 600,
                color: "#333",
              }
            : {
                show: false,
                position: "outside",
                formatter: "{b}",
                fontSize: 10,
                fontWeight: 600,
                color: "#555",
                overflow: "truncate",
                ellipsis: "...",
                width: 70,
              },
          labelLine: {
            show: showPercentOnly,
            length: 10,
            length2: 14,
            smooth: true,
            lineStyle: {
              color: "#ccc",
            },
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 16,
              shadowColor: "rgba(0,0,0,0.15)",
            },
            scaleSize: 6,
          },
          animationType: "scale",
          animationEasing: "elasticOut",
          animationDelay: () => Math.random() * 200,
          data: pieData,
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    const el = chartRef.current;
    const ro =
      typeof ResizeObserver !== "undefined" && el
        ? new ResizeObserver(() => chart.resize())
        : null;
    if (ro && el) ro.observe(el);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (ro && el) ro.unobserve(el);
      chart.dispose();
    };
  }, [pieData, stat.label, stat.decimals, sliceLabels]);

  return (
    <div className={styles["pie-item"]}>
      <div className={styles["pie-item__label"]}>
        <span className={styles["pie-item__stat-name"]}>{stat.label}</span>
        <span className={styles["pie-item__total"]}>
          Total: {formatBenchmarkChartValue(total, stat.decimals ?? 0)}
        </span>
      </div>
      <div className={styles["pie-item__chart"]} ref={chartRef} />
    </div>
  );
};

const PieChart = ({
  rawData,
  institutionIds,
  showHeader = true,
  sliceLabels = "none",
  sectionId: sectionIdProp,
  stats = BENCHMARK_PIE_STATS,
  colorMap: colorMapProp,
}) => {
  const sectionId =
    sectionIdProp === undefined ? "pie-chart-section" : sectionIdProp || undefined;
  const chartData = useMemo(
    () => aggregateBenchmarkByInstitution(rawData, institutionIds),
    [rawData, institutionIds]
  );

  const colorMap = useMemo(() => {
    if (colorMapProp) return colorMapProp;
    const map = {};
    institutionIds.forEach((id, idx) => {
      map[id] = BENCHMARK_INSTITUTION_COLORS[idx % BENCHMARK_INSTITUTION_COLORS.length];
    });
    return map;
  }, [institutionIds, colorMapProp]);

  const visibleStats = useMemo(
    () => stats.filter((stat) => chartData.some((inst) => (inst[stat.key] || 0) > 0)),
    [stats, chartData]
  );

  if (!chartData.length) return null;

  const rootClass = [
    styles["pie-chart"],
    !showHeader && styles["pie-chart--embed"],
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass} id={sectionId || undefined}>
      {showHeader ? (
        <div className={styles["pie-chart__header"]}>
          <div>
            <h3 className={styles["pie-chart__title"]}>
              Proportional Distribution
            </h3>
          </div>
        </div>
      ) : null}

      <div className={styles["pie-chart__grid"]}>
        {visibleStats.map((stat) => (
          <SinglePie
            key={stat.key}
            stat={stat}
            institutions={chartData}
            colorMap={colorMap}
            sliceLabels={sliceLabels}
          />
        ))}
      </div>
    </div>
  );
};

export default PieChart;
